// src/components/CreatePostDialog.tsx
import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Stack,
    Chip,
    ImageList,
    ImageListItem,
    LinearProgress,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreateIcon from '@mui/icons-material/Create';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SchoolIcon from '@mui/icons-material/School';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import VideocamIcon from '@mui/icons-material/Videocam';
import liturgicalService from '../services/liturgicalService';
import { CreatePostPayload } from '../types/liturgical';
import { Eglise } from '../types/auth';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';

const MAX_MEDIA_FILES = 10;
const MAX_FILE_SIZE_MB = 50; // 50MB max per file

interface MediaFile {
    file: File;
    preview: string;
    type: 'image' | 'video';
}

interface CreatePostDialogProps {
    open: boolean;
    onClose: () => void;
    selectedDate: { day: number; month: number; year: number };
    onSuccess: () => void;
    userEglises: Eglise[];
}

const CreatePostDialog: React.FC<CreatePostDialogProps> = ({
    open,
    onClose,
    selectedDate,
    onSuccess,
    userEglises
}) => {
    const { t } = useTranslation();
    const { success, error: showError, warning } = useNotification();

    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadingIndex, setUploadingIndex] = useState(-1);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Form state
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [postType, setPostType] = useState<'reflection' | 'prayer' | 'testimony' | 'teaching'>('reflection');
    const [visibility, setVisibility] = useState<'public' | 'church' | 'private'>('public');
    const [egliseId, setEgliseId] = useState<number | null>(null);
    const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);

    // Validation errors
    const [contentError, setContentError] = useState<string | null>(null);
    const [mediaError, setMediaError] = useState<string | null>(null);

    const POST_TYPES = [
        { value: 'reflection', label: t('dialogs.createPost.types.reflection'), icon: <LightbulbOutlinedIcon />, color: '#2196F3' },
        { value: 'prayer', label: t('dialogs.createPost.types.prayer'), icon: <AutoAwesomeIcon />, color: '#9C27B0' },
        { value: 'testimony', label: t('dialogs.createPost.types.testimony'), icon: <FavoriteIcon />, color: '#E91E63' },
        { value: 'teaching', label: t('dialogs.createPost.types.teaching'), icon: <SchoolIcon />, color: '#FF9800' },
    ];

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setMediaError(null);
        const newMediaFiles: MediaFile[] = [];
        const errors: string[] = [];

        // Check if adding these files would exceed the limit
        const remainingSlots = MAX_MEDIA_FILES - mediaFiles.length;
        if (files.length > remainingSlots) {
            errors.push(t('dialogs.createPost.maxFilesError', { max: MAX_MEDIA_FILES }) || `Maximum ${MAX_MEDIA_FILES} fichiers autorisés`);
        }

        const filesToProcess = Array.from(files).slice(0, remainingSlots);

        for (const file of filesToProcess) {
            // Validate file size
            const fileSizeMB = file.size / (1024 * 1024);
            if (fileSizeMB > MAX_FILE_SIZE_MB) {
                errors.push(`${file.name}: ${t('dialogs.createPost.fileTooLarge', { max: MAX_FILE_SIZE_MB }) || `Fichier trop volumineux (max ${MAX_FILE_SIZE_MB}MB)`}`);
                continue;
            }

            // Determine file type
            const isImage = file.type.startsWith('image/');
            const isVideo = file.type.startsWith('video/');

            if (!isImage && !isVideo) {
                errors.push(`${file.name}: ${t('dialogs.createPost.invalidFileType') || 'Type de fichier non supporté'}`);
                continue;
            }

            // Create preview URL
            const preview = URL.createObjectURL(file);
            newMediaFiles.push({
                file,
                preview,
                type: isImage ? 'image' : 'video'
            });
        }

        if (errors.length > 0) {
            setMediaError(errors.join('\n'));
        }

        if (newMediaFiles.length > 0) {
            setMediaFiles(prev => [...prev, ...newMediaFiles]);
        }

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveMedia = (index: number) => {
        setMediaFiles(prev => {
            const newFiles = [...prev];
            // Revoke the preview URL to free memory
            URL.revokeObjectURL(newFiles[index].preview);
            newFiles.splice(index, 1);
            return newFiles;
        });
        setMediaError(null);
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const files = event.dataTransfer.files;
        if (files.length > 0) {
            // Create a synthetic event to reuse handleFileChange
            const input = fileInputRef.current;
            if (input) {
                const dataTransfer = new DataTransfer();
                Array.from(files).forEach(file => dataTransfer.items.add(file));
                input.files = dataTransfer.files;
                handleFileChange({ target: input } as React.ChangeEvent<HTMLInputElement>);
            }
        }
    };

    const handleSubmit = async () => {
        // Validation
        if (!content.trim()) {
            setContentError(t('validation.contentRequired'));
            return;
        }

        setLoading(true);
        setContentError(null);
        setMediaError(null);
        setUploadProgress(0);
        setUploadingIndex(-1);

        try {
            // Step 1: Create the post
            const postData: CreatePostPayload = {
                ethiopian_month: selectedDate.month,
                ethiopian_day: selectedDate.day,
                ethiopian_year: null,
                title: title.trim() || undefined,
                content: content.trim(),
                post_type: postType,
                visibility,
                eglise_id: visibility === 'church' ? egliseId : null
            };

            const response = await liturgicalService.createPost(postData);
            const createdPost = response.data;

            // Step 2: Upload all media files
            if (mediaFiles.length > 0) {
                let uploadedCount = 0;
                const failedUploads: string[] = [];

                for (let i = 0; i < mediaFiles.length; i++) {
                    const media = mediaFiles[i];
                    setUploadingIndex(i);
                    setUploadProgress(Math.round((i / mediaFiles.length) * 100));

                    try {
                        await liturgicalService.uploadMedia(media.file, {
                            post_id: createdPost.id,
                            caption: title.trim() || `Media ${i + 1}`
                        });
                        uploadedCount++;
                    } catch (uploadErr: any) {
                        console.error(`Failed to upload media ${i + 1}:`, uploadErr);
                        failedUploads.push(media.file.name);
                    }
                }

                setUploadProgress(100);

                // Show appropriate notification based on upload results
                if (failedUploads.length === 0) {
                    success(t('dialogs.createPost.successWithMedia', { count: uploadedCount }) ||
                        `Post créé avec ${uploadedCount} média(s)`);
                } else if (uploadedCount > 0) {
                    warning(t('dialogs.createPost.partialUpload', {
                        uploaded: uploadedCount,
                        failed: failedUploads.length
                    }) || `Post créé. ${uploadedCount} média(s) uploadé(s), ${failedUploads.length} échoué(s)`);
                } else {
                    showError(t('dialogs.createPost.allUploadsFailed') ||
                        'Post créé mais tous les médias ont échoué');
                }
            } else {
                // No media, just show success
                success(t('dialogs.createPost.success'));
            }

            // Callback and close
            onSuccess();
            handleClose();
        } catch (err: any) {
            const errorMessage = err.response?.data?.detail || t('dialogs.createPost.error');
            showError(errorMessage);
        } finally {
            setLoading(false);
            setUploadingIndex(-1);
            setUploadProgress(0);
        }
    };

    const handleClose = () => {
        if (!loading) {
            // Clean up preview URLs
            mediaFiles.forEach(media => URL.revokeObjectURL(media.preview));

            // Reset form
            setTitle('');
            setContent('');
            setPostType('reflection');
            setVisibility('public');
            setEgliseId(null);
            setMediaFiles([]);
            setContentError(null);
            setMediaError(null);
            setUploadProgress(0);
            setUploadingIndex(-1);
            onClose();
        }
    };

    const selectedPostType = POST_TYPES.find(t => t.value === postType);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{ p: 0 }}>
                <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Box sx={{ p: 1.5, bgcolor: '#f3e8ff', borderRadius: 1 }}>
                                <CreateIcon sx={{ color: '#7c3aed', fontSize: 24 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight={600} color="#1e293b">
                                    {t('dialogs.createPost.title')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t('dialogs.createPost.forDate', {
                                        day: selectedDate.day,
                                        month: selectedDate.month,
                                        year: selectedDate.year
                                    })}
                                </Typography>
                            </Box>
                        </Stack>
                        <IconButton onClick={handleClose} size="small" disabled={loading}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Stack spacing={3}>
                    {/* Post type */}
                    <Box>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom color="#64748b">
                            {t('dialogs.createPost.postTypeLabel')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {POST_TYPES.map((type) => (
                                <Chip
                                    key={type.value}
                                    icon={type.icon}
                                    label={type.label}
                                    onClick={() => setPostType(type.value as any)}
                                    color={postType === type.value ? 'primary' : 'default'}
                                    variant={postType === type.value ? 'filled' : 'outlined'}
                                    sx={{
                                        ...(postType === type.value && {
                                            bgcolor: type.color,
                                            color: 'white',
                                            '& .MuiChip-icon': { color: 'white' },
                                            '&:hover': { bgcolor: type.color }
                                        })
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Title (optional) */}
                    <TextField
                        fullWidth
                        label={t('dialogs.createPost.titleLabel')}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={`${t('common.example') || 'Ex'}: ${selectedPostType?.label.toLowerCase() || ''}`}
                        sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: 1.5 }
                        }}
                    />

                    {/* Content (required) */}
                    <TextField
                        fullWidth
                        required
                        multiline
                        rows={6}
                        label={t('dialogs.createPost.contentLabel')}
                        value={content}
                        onChange={(e) => {
                            setContent(e.target.value);
                            if (contentError) setContentError(null);
                        }}
                        error={!!contentError}
                        helperText={contentError || t('validation.required')}
                        sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: 1.5 }
                        }}
                    />

                    {/* Visibility */}
                    <FormControl fullWidth>
                        <InputLabel>{t('dialogs.createPost.visibilityLabel')}</InputLabel>
                        <Select
                            value={visibility}
                            onChange={(e) => {
                                setVisibility(e.target.value as any);
                                if (e.target.value !== 'church') {
                                    setEgliseId(null);
                                }
                            }}
                            label={t('dialogs.createPost.visibilityLabel')}
                            sx={{ borderRadius: 1.5 }}
                        >
                            <MenuItem value="public">{t('dialogs.createPost.visibility.public')}</MenuItem>
                            <MenuItem value="church" disabled={userEglises.length === 0}>
                                {t('dialogs.createPost.visibility.church')}
                            </MenuItem>
                            <MenuItem value="private">{t('dialogs.createPost.visibility.private')}</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Church selection if visibility = church */}
                    {visibility === 'church' && userEglises.length > 0 && (
                        <FormControl fullWidth>
                            <InputLabel>{t('dialogs.createPost.selectChurchLabel')}</InputLabel>
                            <Select
                                value={egliseId || ''}
                                onChange={(e) => setEgliseId(Number(e.target.value))}
                                label={t('dialogs.createPost.selectChurchLabel')}
                                sx={{ borderRadius: 1.5 }}
                            >
                                {userEglises.map((eglise) => (
                                    <MenuItem key={eglise.id} value={eglise.id}>
                                        {eglise.nom}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Media upload zone */}
                    <Box>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom color="#64748b">
                            {t('dialogs.createPost.mediaLabel') || 'Médias'} ({mediaFiles.length}/{MAX_MEDIA_FILES})
                        </Typography>

                        <input
                            ref={fileInputRef}
                            accept="image/*,video/*"
                            style={{ display: 'none' }}
                            id="media-upload"
                            type="file"
                            multiple
                            onChange={handleFileChange}
                        />

                        {/* Drop zone */}
                        <Box
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            sx={{
                                border: '2px dashed',
                                borderColor: mediaFiles.length >= MAX_MEDIA_FILES ? '#e2e8f0' : '#cbd5e1',
                                borderRadius: 2,
                                p: 2,
                                textAlign: 'center',
                                bgcolor: mediaFiles.length >= MAX_MEDIA_FILES ? '#f8fafc' : '#fafbfc',
                                cursor: mediaFiles.length >= MAX_MEDIA_FILES ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': mediaFiles.length < MAX_MEDIA_FILES ? {
                                    borderColor: '#7c3aed',
                                    bgcolor: '#f5f3ff',
                                } : {},
                            }}
                            onClick={() => mediaFiles.length < MAX_MEDIA_FILES && fileInputRef.current?.click()}
                        >
                            <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                                <AddPhotoAlternateIcon sx={{ fontSize: 32, color: mediaFiles.length >= MAX_MEDIA_FILES ? '#cbd5e1' : '#7c3aed' }} />
                                <Box>
                                    <Typography variant="body2" fontWeight={500} color={mediaFiles.length >= MAX_MEDIA_FILES ? 'text.disabled' : 'text.primary'}>
                                        {mediaFiles.length >= MAX_MEDIA_FILES
                                            ? (t('dialogs.createPost.maxFilesReached') || 'Limite atteinte')
                                            : (t('dialogs.createPost.dropOrClick') || 'Glissez ou cliquez pour ajouter')
                                        }
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('dialogs.createPost.mediaHint') || `Images et vidéos, max ${MAX_FILE_SIZE_MB}MB chacun`}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        {/* Media error */}
                        {mediaError && (
                            <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 1.5 }} onClose={() => setMediaError(null)}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                    {mediaError}
                                </Typography>
                            </Alert>
                        )}

                        {/* Media preview grid */}
                        {mediaFiles.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <ImageList
                                    cols={mediaFiles.length === 1 ? 1 : mediaFiles.length === 2 ? 2 : 3}
                                    gap={8}
                                    sx={{ m: 0 }}
                                >
                                    {mediaFiles.map((media, index) => (
                                        <ImageListItem
                                            key={index}
                                            sx={{
                                                position: 'relative',
                                                borderRadius: 1.5,
                                                overflow: 'hidden',
                                                border: '1px solid #e2e8f0',
                                            }}
                                        >
                                            {media.type === 'image' ? (
                                                <img
                                                    src={media.preview}
                                                    alt={`Preview ${index + 1}`}
                                                    loading="lazy"
                                                    style={{
                                                        width: '100%',
                                                        height: mediaFiles.length === 1 ? 200 : 120,
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        height: mediaFiles.length === 1 ? 200 : 120,
                                                        bgcolor: '#1e293b',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <VideocamIcon sx={{ fontSize: 40, color: 'white' }} />
                                                </Box>
                                            )}

                                            {/* Upload progress overlay */}
                                            {loading && uploadingIndex === index && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        bgcolor: 'rgba(0,0,0,0.5)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <CircularProgress size={30} sx={{ color: 'white' }} />
                                                </Box>
                                            )}

                                            {/* Delete button */}
                                            {!loading && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveMedia(index);
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                        bgcolor: 'rgba(239, 68, 68, 0.9)',
                                                        color: 'white',
                                                        '&:hover': { bgcolor: '#dc2626' },
                                                        width: 24,
                                                        height: 24,
                                                    }}
                                                >
                                                    <CloseIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            )}

                                            {/* File type badge */}
                                            <Chip
                                                label={media.type === 'image' ? 'IMG' : 'VID'}
                                                size="small"
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: 4,
                                                    left: 4,
                                                    height: 20,
                                                    fontSize: '0.65rem',
                                                    bgcolor: media.type === 'image' ? 'rgba(16, 185, 129, 0.9)' : 'rgba(99, 102, 241, 0.9)',
                                                    color: 'white',
                                                }}
                                            />
                                        </ImageListItem>
                                    ))}
                                </ImageList>

                                {/* Upload progress bar */}
                                {loading && uploadingIndex >= 0 && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('dialogs.createPost.uploading') || 'Upload en cours...'} ({uploadingIndex + 1}/{mediaFiles.length})
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {uploadProgress}%
                                            </Typography>
                                        </Stack>
                                        <LinearProgress
                                            variant="determinate"
                                            value={uploadProgress}
                                            sx={{
                                                height: 6,
                                                borderRadius: 3,
                                                bgcolor: '#e2e8f0',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 3,
                                                    bgcolor: '#7c3aed',
                                                },
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ borderRadius: 1.5 }}
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !content.trim()}
                    startIcon={loading && <CircularProgress size={18} color="inherit" />}
                    sx={{
                        borderRadius: 1.5,
                        bgcolor: '#7c3aed',
                        '&:hover': { bgcolor: '#6d28d9' },
                        minWidth: 120,
                    }}
                >
                    {loading ? t('dialogs.createPost.publishing') : t('dialogs.createPost.publishButton')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreatePostDialog;
